import React, { useRef } from "react";

import EmptyCardGrowth from "assets/images/EmptyCardGrowth.png";
import HighchartsReact from "highcharts-react-official";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { Chart } from "components/reports-cashflow/Chart/Chart";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { CashflowGraph } from "store/types";

interface IProps {
  loading: boolean;
  error: boolean;
  isEmpty: boolean;
  graph: CashflowGraph | undefined;
  onRetry: () => void;
  /** Chart height — summary section is shorter than the full view. */
  height?: number;
}

const StateWrap: React.FC<{ height: number; children: React.ReactNode }> = ({ height, children }) => (
  <Stack alignItems="center" justifyContent="center" gap="12px" sx={{ height }}>
    {children}
  </Stack>
);

export const CashflowForecastSummary: React.FC<IProps> = ({ loading, error, isEmpty, graph, onRetry, height = 260 }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  if (loading) {
    return (
      <StateWrap height={height}>
        <CircularProgress />
      </StateWrap>
    );
  }

  if (error) {
    return (
      <StateWrap height={height}>
        <Typography sx={{ color: "#5d7782", fontSize: "14px" }}>Couldn’t load the cashflow forecast.</Typography>
        <Button variant="outlined" onClick={onRetry} sx={{ textTransform: "none" }}>
          Retry
        </Button>
      </StateWrap>
    );
  }

  if (isEmpty) {
    return (
      <EmptyCard
        title={EMPTY_CARD_CONTENT.portfolioCashflow.title}
        description={{
          text: EMPTY_CARD_CONTENT.portfolioCashflow.description,
          externalText: EMPTY_CARD_CONTENT.portfolioCashflow.externalDescription
        }}
        image={{ src: EmptyCardGrowth, sx: { width: 240, height: 107 } }}
        contentWidth={460}
        sx={{ flexGrow: 0, bgcolor: "transparent", minHeight: height }}
      />
    );
  }

  return (
    <Stack sx={{ height }}>
      <Chart data={graph} chartRef={chartRef} aumData={null} />
    </Stack>
  );
};
